/* eslint-disable react/display-name */
import { useEffect, useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import _ from 'lodash';

import { RoomsContentLoader } from '../shared';

import { roomTypesDomain, portfolioDomain } from '../../domain';

import FeaturedSpacesErrorResults from './featured-spaces-error.component';

function LazyFeaturedSpaces(props) {
    const {
        isHome,
        title,
        type,
        id,
        visibleRooms,
        text,
        city,
        isGrid,
        portfolioId,
        selectedCity,
        selectedBuilding,
        selectedRoomType,
        isLanding
    } = props;    

    const [searchFinished, setSearchFinished] = useState(false);

    const [roomTypings, setRoomTypes] = useState([]);

    const [allPortfolios, setAllPortfolios] = useState([]);    

    const [error, setError] = useState(null);

    const router = useRouter();    

    const allRoomTypes = useMemo(() => _.map(roomTypings, (roomType) => roomType), [roomTypings]);

    useEffect(() => {

        if (!selectedCity) return;

        const asyncSearch = async () => {

            if (type === 'featured_building') {
                try {
                    const rooms = await roomTypesDomain.checkRoomTypeAvailabilityPerBuilding(selectedBuilding)
                    setRoomTypes(rooms);                    
                } catch (err) {
                    setError(err);
                }
            } else {                
                try {
                    const rooms = await roomTypesDomain.fetchAvailabilityForCity(_.get(selectedCity, 'id'));
                    setRoomTypes(rooms);
                } catch (err) {
                    setError(err);
                }
            }

            const portfolios = await portfolioDomain.fetchAllPortfolios();
            setAllPortfolios(portfolios);

            setSearchFinished(true);
        };        

        asyncSearch();

    }, [selectedCity, type]);


    // Safari fix: weird behaviour with anchor tags and lazy loading combo where there would be jumps to wrong location
    // https://github.com/vercel/next.js/issues/11109
    useEffect(() => {
        const path = window.location.hash;
        if (router.pathname === '/find-a-space' && path && path.includes('#')) {
            setTimeout(() => {
                const id = path.replace('#', '');
                const el = window.document.getElementById(id);
                if (!el) return;
                const r = el.getBoundingClientRect();
                window.top.scroll({
                    top: window.pageYOffset + r.top,
                    behavior: 'smooth',
                });
            }, 1);
        }
    }, [router.pathname]);

    const LazyLoadFeaturedDefault = dynamic(() => import('./featured-spaces.component'), 
    {
        ssr: false,
        loading: () => <RoomsContentLoader isGrid={isGrid ?? false} />,
    });

    const LazyLoadFeaturedBuildings = dynamic(() => import('./featured-spaces-building.component'),
    {
        ssr: false,
        loading: () => <RoomsContentLoader isGrid={isGrid ?? false} />,
    });

    const LazyLoadFeaturedSimilar = dynamic(() => import('./similar-spaces-in-city.component'), 
    {
        ssr: false,
        loading: () => <RoomsContentLoader isGrid={isGrid ?? false} />,
    });

    const LazyLoad = (props) => {
        const { type, ...rest } = props;
        switch (type) {
            case 'featured_building':
                return <LazyLoadFeaturedBuildings {...rest} />;
            case 'featured_similar_spaces':
                return <LazyLoadFeaturedSimilar {...rest} />;
            default:
                return <LazyLoadFeaturedDefault {...rest} />;
        }
    };

    if (error) {        
        return <FeaturedSpacesErrorResults 
            id={id}
            title="Error loading featured spaces for"
            isHome={isHome}           
            selectedCity={selectedCity}
            errorMessage={error?.message}
            isLanding={isLanding}
        />;
    }

    return (
        <>
            {searchFinished ? (
             <LazyLoad
                id={id}
                visibleRooms={visibleRooms}
                text={text}
                city={city}
                type={type}
                isHome={isHome}
                title={title}
                portfolioId={portfolioId}
                selectedCity={selectedCity}
                roomTypes={allRoomTypes}
                isGrid={isGrid}
                allPortfolios={allPortfolios}
                selectedBuilding={selectedBuilding}
                selectedRoomType={selectedRoomType}
                isLanding={isLanding}
            />
            ) : (<RoomsContentLoader isGrid={isGrid ?? false} />)
            }
        </>

    );
}

export default LazyFeaturedSpaces;

LazyFeaturedSpaces.propTypes = {
    isHome: PropTypes.bool,
    title: PropTypes.string,
    type: PropTypes.string,
    id: PropTypes.string,
    visibleRooms: PropTypes.number,
    text: PropTypes.string,
    city: PropTypes.object,
    isGrid: PropTypes.bool,
    portfolioId: PropTypes.string,
    selectedCity: PropTypes.object,
    selectedBuilding: PropTypes.object,
    selectedRoomType: PropTypes.object,
    isLanding: PropTypes.bool
};
